/**
 * Looking back — what actually happened, over a range of months.
 *
 * Three questions, three forms, chosen to match what each one is asking:
 *
 * - **Income against outgoings** is polarity, so the bars diverge from a shared
 *   baseline: money in above it, money out below. Direction carries the meaning,
 *   which means the two series stay legible without relying on colour.
 * - **Where it went** and **who got it** are rankings, so they are horizontal
 *   bars with the amounts written on every row — a bar you have to hover to read
 *   is a bar that failed.
 * - **Net worth** is change over time, so it is one line. Assets and liabilities
 *   are figures underneath rather than two more lines, because the question is
 *   "is this going up", not "how do three series relate".
 *
 * The month control in the header sets the end of the range, so paging back
 * through months moves the report with it.
 */

import { useQuery } from '@tanstack/react-query'
import { type BudgetMonth, Month } from '@ynab/core'
import { useState } from 'react'
import {
  type CategorySpend,
  fetchReports,
  type MonthlyFlow,
  type NetWorthPoint,
  type PayeeSpend,
  queryKeys,
} from '../api.ts'
import { FlowSankey } from '../components/FlowSankey.tsx'
import { fmt, fmtWhole } from '../lib/format.ts'
import { useStickyColumn } from '../lib/useStickyColumn.ts'

/** Rows shown before a ranking asks you to opt into the long tail. */
const RANKED_PREVIEW = 8

const RANGES = [
  { id: 'last', label: 'Last month', months: 1 },
  { id: '3m', label: '3 months', months: 3 },
  { id: '6m', label: '6 months', months: 6 },
  { id: '12m', label: '12 months', months: 12 },
  { id: 'ytd', label: 'This year', months: null },
] as const

type RangeId = (typeof RANGES)[number]['id']

function resolveRange(id: RangeId, anchor: BudgetMonth): { from: BudgetMonth; to: BudgetMonth } {
  /*
   * The only range that does not end at the month you are on.
   *
   * Every other option is "the last N months including this one", which for the
   * current month is a partial figure — the month is still happening. Last month is
   * the finished one, which is what you want when the question is "how did we actually
   * do", so it moves both ends back rather than just the start.
   */
  if (id === 'last') {
    const previous = Month.addMonths(anchor, -1)
    return { from: previous, to: previous }
  }
  if (id === 'ytd') {
    const { year } = Month.toParts(anchor)
    return { from: Month.fromParts(year, 1), to: anchor }
  }
  const span = RANGES.find((range) => range.id === id)?.months ?? 12
  return { from: Month.addMonths(anchor, -(span - 1)), to: anchor }
}

export function LookingBack({ month }: { month: string }) {
  const [rangeId, setRangeId] = useState<RangeId>('6m')
  const { from, to } = resolveRange(rangeId, Month.budgetMonth(month))

  const reports = useQuery({
    queryKey: queryKeys.reports(from, to),
    queryFn: () => fetchReports(from, to),
  })

  const data = reports.data
  const totals = data?.totals
  const latest = data?.netWorth.at(-1)
  // Everything is derived from transactions, so one empty range means one empty
  // screen — say so rather than drawing four charts of nothing.
  const empty =
    data !== undefined &&
    data.totals.income === 0 &&
    data.totals.spending === 0 &&
    data.netWorth.every((point) => point.net === 0)

  const stickyColumn = useStickyColumn<HTMLElement>()

  return (
    <section className="flex flex-col gap-[18px] px-4 pt-[22px] pb-[60px] sm:px-7">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-5">
        <div className="flex flex-col gap-1.5">
          <h2 className="m-0 font-display text-[34px] font-normal leading-none">Looking back</h2>
          <p className="m-0 max-w-[640px] text-[14px] text-muted">
            {Month.formatLong(from)} to {Month.formatLong(to)}. Transfers and opening balances are
            left out of income and spending — moving your own money is neither.
          </p>
        </div>

        <div className="flex rounded-full bg-segment p-[3px]">
          {RANGES.map((range) => (
            <button
              key={range.id}
              type="button"
              onClick={() => setRangeId(range.id)}
              className={`whitespace-nowrap rounded-full px-[15px] py-[7px] text-[12.5px] ${
                rangeId === range.id
                  ? 'bg-segment-active font-medium text-ink shadow-[var(--shadow-raise)]'
                  : 'bg-transparent text-muted hover:text-ink'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {reports.isPending && <p className="text-[14px] text-muted">Adding it all up…</p>}

      {reports.isError && (
        <p className="rounded-card bg-blush-soft p-4 text-[14px] text-blush-deep">
          {reports.error instanceof Error ? reports.error.message : 'Could not build the report.'}
        </p>
      )}

      {empty && (
        <div className="max-w-[560px] rounded-card border border-line-sand bg-card p-6">
          <h3 className="m-0 font-display text-[22px] font-normal">Nothing to look back on yet</h3>
          <p className="mt-2 mb-0 text-[14px] leading-relaxed text-muted">
            No transactions landed in these months. Sync a bank feed or add a few by hand, and this
            fills in on its own — none of it is stored, it is all counted from the ledger on read.
          </p>
        </div>
      )}

      {data && totals && !empty && (
        <>
          <div className="grid grid-cols-4 gap-3">
            <Stat label="Came in" value={fmt(totals.income)} />
            <Stat label="Went out" value={fmt(totals.spending)} />
            <Stat
              label={totals.net < 0 ? 'Short by' : 'Kept'}
              value={fmt(Math.abs(totals.net))}
              tone={totals.net < 0 ? 'alert' : 'good'}
            />
            <Stat
              label={`Average month (${totals.monthCount})`}
              value={fmt(totals.averageSpending)}
              note="spent"
            />
          </div>

          <div className="grid grid-cols-1 items-start gap-[22px] xl:grid-cols-[minmax(0,1fr)_340px]">
            <div className="flex flex-col gap-[18px] lg:min-w-[640px]">
              <CashFlow months={data.months} />
              <FlowSankey
                income={data.totals.income}
                byIncomeSource={data.byIncomeSource}
                byCategory={data.byCategory}
              />
              <Ranked
                title="Where it went"
                caption="By envelope, biggest first."
                items={data.byCategory.map((entry) => ({
                  key: entry.categoryId ?? 'uncategorised',
                  name: entry.name,
                  sub: subtitleFor(entry),
                  amount: entry.spent,
                }))}
              />
              <Ranked
                title="Who got it"
                caption="By payee. Everything past the top 40 is rolled into one row."
                items={data.byPayee.map((entry: PayeeSpend) => ({
                  key: entry.name,
                  name: entry.name,
                  sub: entry.rollup
                    ? 'the long tail'
                    : `${entry.transactions} ${entry.transactions === 1 ? 'time' : 'times'}`,
                  amount: entry.spent,
                }))}
              />
            </div>

            <aside ref={stickyColumn} className="sticky top-[96px] flex flex-col gap-3.5">
              <NetWorth points={data.netWorth} />

              {latest && (
                <div className="flex flex-col gap-2.5 rounded-card border border-line bg-card p-[18px]">
                  {/* Named rather than "as it stands": the range can end before today, and
                      a transaction dated after it is genuinely not in this figure. */}
                  <span className="text-[10px] uppercase tracking-[0.16em] text-muted">
                    At the end of {latest.label}
                  </span>
                  <Line label="Held" value={fmt(latest.assets)} />
                  <Line label="Owed" value={fmt(latest.liabilities)} tone="alert" />
                  <div className="h-px bg-line" />
                  <Line label="Net worth" value={fmt(latest.net)} strong />
                  <p className="m-0 text-[12px] leading-[1.5] text-muted">
                    Every account, tracking ones included — that is what makes this net worth rather
                    than a budget figure.
                  </p>
                </div>
              )}
            </aside>
          </div>
        </>
      )}
    </section>
  )
}

function subtitleFor(entry: CategorySpend): string {
  if (entry.categoryId === null) return 'nobody has sorted these yet'
  return entry.groupName ?? ''
}

function Stat({
  label,
  value,
  note,
  tone = 'plain',
}: {
  label: string
  value: string
  note?: string
  tone?: 'plain' | 'good' | 'alert'
}) {
  return (
    <div className="flex flex-col gap-1 rounded-card border border-line bg-card px-[18px] py-3.5">
      <span className="text-[10px] uppercase tracking-[0.14em] text-muted">{label}</span>
      <span
        className={`font-display text-[26px] leading-none ${
          tone === 'alert' ? 'text-blush-deep' : tone === 'good' ? 'text-sky-deep' : 'text-ink'
        }`}
      >
        {value}
      </span>
      {note && <span className="text-[11.5px] text-muted">{note}</span>}
    </div>
  )
}

function Line({
  label,
  value,
  tone = 'plain',
  strong = false,
}: {
  label: string
  value: string
  tone?: 'plain' | 'alert'
  strong?: boolean
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[12.5px] text-ink-dim">{label}</span>
      <span
        className={`${strong ? 'font-display text-[19px]' : 'text-[14px]'} ${
          tone === 'alert' ? 'text-blush-deep' : 'text-ink'
        }`}
      >
        {value}
      </span>
    </div>
  )
}

/** Half the plot height in pixels. Both directions get the same room, so the two sides compare. */
const HALF = 92

/**
 * Income against outgoings.
 *
 * One scale for both directions, so a month that earned $4k and spent $2k is
 * visibly twice as tall above the line as below it. Two scales would let the
 * spending bar look bigger than the income bar that paid for it.
 */
function CashFlow({ months }: { months: MonthlyFlow[] }) {
  const [hovered, setHovered] = useState<number | null>(null)
  const [showNumbers, setShowNumbers] = useState(false)

  const scale = Math.max(1, ...months.flatMap((m) => [m.income, m.spending]))
  const height = (value: number) => (value <= 0 ? 0 : Math.max(2, (value / scale) * HALF))
  const active = hovered === null ? null : months[hovered]

  return (
    <div className="flex flex-col gap-3 rounded-card border border-line bg-card p-[18px]">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-0.5">
          <h3 className="m-0 text-[15px] font-medium">Income against outgoings</h3>
          <span className="text-[12px] text-muted">
            Above the line came in, below it went out. Peak month {fmtWhole(scale)}.
          </span>
        </div>
        <div className="flex items-center gap-4">
          <Legend swatch="bg-sky" label="Money in" />
          <Legend swatch="bg-slate" label="Money out" />
          <button
            type="button"
            onClick={() => setShowNumbers((shown) => !shown)}
            className="whitespace-nowrap rounded-full bg-sand px-3 py-1.5 text-[11.5px] text-ink-dim transition-colors hover:bg-sand-deep"
          >
            {showNumbers ? 'Show the chart' : 'Show the numbers'}
          </button>
        </div>
      </div>

      {showNumbers ? (
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.12em] text-muted">
              <th className="py-1.5 text-left font-normal">Month</th>
              <th className="py-1.5 text-right font-normal">In</th>
              <th className="py-1.5 text-right font-normal">Out</th>
              <th className="py-1.5 text-right font-normal">Net</th>
            </tr>
          </thead>
          <tbody>
            {months.map((month) => (
              <tr key={month.month} className="border-t border-line-faint">
                <td className="py-1.5 text-left">{month.label}</td>
                <td className="py-1.5 text-right">{fmt(month.income)}</td>
                <td className="py-1.5 text-right">{fmt(month.spending)}</td>
                <td
                  className={`py-1.5 text-right ${month.net < 0 ? 'text-blush-deep' : 'text-ink'}`}
                >
                  {fmt(month.net)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="relative">
          {active && hovered !== null && (
            <div
              // Beside the column, never over it — a tooltip that hides the bar it
              // is describing makes you hover twice to see one month.
              className={`kete-fade pointer-events-none absolute top-0 z-10 w-[168px] rounded-[10px] border border-line bg-card p-2.5 shadow-[var(--shadow-tooltip)] ${
                hovered > months.length / 2 - 1
                  ? '-translate-x-[calc(100%+22px)]'
                  : 'translate-x-[22px]'
              }`}
              style={{ left: `${((hovered + 0.5) / months.length) * 100}%` }}
            >
              <div className="mb-1 text-[11px] uppercase tracking-[0.12em] text-muted">
                {active.label}
              </div>
              <Line label="In" value={fmt(active.income)} />
              <Line label="Out" value={fmt(active.spending)} />
              <Line
                label="Net"
                value={fmt(active.net)}
                {...(active.net < 0 ? { tone: 'alert' as const } : {})}
              />
            </div>
          )}

          <div className="flex items-stretch gap-1.5">
            {months.map((month, index) => (
              <button
                key={month.month}
                type="button"
                onMouseEnter={() => setHovered(index)}
                onMouseLeave={() => setHovered(null)}
                onFocus={() => setHovered(index)}
                onBlur={() => setHovered(null)}
                className={`flex flex-1 flex-col rounded-[6px] px-0.5 pt-1 pb-0 transition-colors ${
                  hovered === index ? 'bg-paper' : 'bg-transparent'
                }`}
                title={`${month.label}: ${fmt(month.income)} in, ${fmt(month.spending)} out`}
              >
                <span className="flex w-full items-end justify-center" style={{ height: HALF }}>
                  <span
                    className="w-full max-w-[30px] rounded-t-[4px] bg-sky"
                    style={{ height: height(month.income) }}
                  />
                </span>
                {/* Own 1px margins, so the two fills never touch across the baseline. */}
                <span className="my-px h-px w-full bg-line-strong" />
                <span className="flex w-full items-start justify-center" style={{ height: HALF }}>
                  <span
                    className="w-full max-w-[30px] rounded-b-[4px] bg-slate"
                    style={{ height: height(month.spending) }}
                  />
                </span>
                <span className="mt-1.5 w-full text-center text-[10px] text-muted">
                  {month.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5 text-[11.5px] text-ink-dim">
      <span className={`h-2.5 w-2.5 rounded-[3px] ${swatch}`} />
      {label}
    </span>
  )
}

interface RankedItem {
  key: string
  name: string
  sub: string
  amount: number
}

/**
 * A ranking, with the amount written on every row.
 *
 * One colour for every bar. Shading by rank would encode the same thing twice and
 * imply a category is a *kind* of thing rather than just this month's biggest.
 */
function Ranked({
  title,
  caption,
  items,
}: {
  title: string
  caption: string
  items: RankedItem[]
}) {
  const [expanded, setExpanded] = useState(false)
  const shown = expanded ? items : items.slice(0, RANKED_PREVIEW)
  const scale = Math.max(1, ...items.map((item) => item.amount))

  return (
    <div className="flex flex-col gap-3 rounded-card border border-line bg-card p-[18px]">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-0.5">
          <h3 className="m-0 text-[15px] font-medium">{title}</h3>
          <span className="text-[12px] text-muted">{caption}</span>
        </div>
        {items.length > RANKED_PREVIEW && (
          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            className="whitespace-nowrap rounded-full bg-sand px-3 py-1.5 text-[11.5px] text-ink-dim transition-colors hover:bg-sand-deep"
          >
            {expanded ? 'Show the top 8' : `Show all ${items.length}`}
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <p className="m-0 py-4 text-[13.5px] text-muted">Nothing spent in this range.</p>
      ) : (
        <div className="flex flex-col">
          {shown.map((item) => (
            <div
              key={item.key}
              className="grid grid-cols-[minmax(0,190px)_minmax(0,1fr)_110px] items-center gap-3 rounded-[6px] py-[7px] transition-colors hover:bg-paper"
            >
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-[13.5px]">{item.name}</span>
                {item.sub && <span className="truncate text-[11px] text-muted">{item.sub}</span>}
              </div>
              <div className="h-[10px] w-full overflow-hidden rounded-[4px] bg-quiet">
                <div
                  className="h-full rounded-[4px] bg-slate"
                  style={{ width: `${Math.max(0, (item.amount / scale) * 100)}%` }}
                />
              </div>
              <span
                className={`text-right text-[13.5px] ${
                  item.amount < 0 ? 'text-sky-deep' : 'text-ink'
                }`}
              >
                {item.amount < 0 ? `${fmt(-item.amount)} back` : fmt(item.amount)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Net worth over the range.
 *
 * The y scale always includes zero. A line chart cropped to its own min and max
 * turns a 2% wobble into a cliff, which is the most common way a net-worth chart
 * lies to the person reading it.
 */
function NetWorth({ points }: { points: NetWorthPoint[] }) {
  const [hovered, setHovered] = useState<number | null>(null)

  if (points.length === 0) {
    return (
      <div className="rounded-card border border-line bg-card p-[18px]">
        <h3 className="m-0 text-[15px] font-medium">Net worth</h3>
        <p className="mt-1 mb-0 text-[12.5px] text-muted">No account history in this range yet.</p>
      </div>
    )
  }

  const values = points.map((point) => point.net)
  const top = Math.max(0, ...values)
  const bottom = Math.min(0, ...values)
  const span = top - bottom || 1
  const anyNegative = bottom < 0

  // A single point has no line to draw, so give it a flat one across the box.
  const x = (index: number) => (points.length === 1 ? 50 : (index / (points.length - 1)) * 100)
  const y = (value: number) => 100 - ((value - bottom) / span) * 100
  const zeroY = y(0)

  const line = points.map((point, index) => `${x(index)},${y(point.net)}`).join(' ')
  const active = hovered === null ? null : points[hovered]
  const change = (points.at(-1)?.net ?? 0) - (points[0]?.net ?? 0)

  return (
    <div className="flex flex-col gap-3 rounded-card border border-line bg-card p-[18px]">
      <div className="flex flex-col gap-0.5">
        <h3 className="m-0 text-[15px] font-medium">Net worth</h3>
        <span className="text-[12px] text-muted">
          {change === 0
            ? 'Flat across this range.'
            : `${change > 0 ? 'Up' : 'Down'} ${fmtWhole(Math.abs(change))} across this range.`}
        </span>
      </div>

      <div className="relative h-[130px]">
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="h-full w-full"
          role="img"
          aria-label={`Net worth from ${points[0]?.label} to ${points.at(-1)?.label}`}
        >
          <title>Net worth over the selected range</title>
          {/* No fill when the line crosses zero: shaded area below the axis reads as a quantity, not a debt. */}
          {!anyNegative && (
            <polygon
              points={`0,${zeroY} ${line} 100,${zeroY}`}
              fill="var(--color-sky-soft)"
              stroke="none"
            />
          )}
          <line
            x1="0"
            x2="100"
            y1={zeroY}
            y2={zeroY}
            stroke="var(--color-line)"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
          <polyline
            points={line}
            fill="none"
            stroke="var(--color-sky-deep)"
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        {active && hovered !== null && (
          <>
            <div
              className="pointer-events-none absolute top-0 bottom-0 w-px bg-line-strong"
              style={{ left: `${x(hovered)}%` }}
            />
            <div
              className="pointer-events-none absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-card bg-sky-deep"
              style={{ left: `${x(hovered)}%`, top: `${y(active.net)}%` }}
            />
            <div className="kete-fade pointer-events-none absolute top-0 right-0 rounded-[8px] border border-line bg-card px-2.5 py-1.5 text-right">
              <div className="text-[10px] uppercase tracking-[0.12em] text-muted">
                {active.label}
              </div>
              <div className="text-[14px]">{fmt(active.net)}</div>
            </div>
          </>
        )}

        {/* One transparent hit target per point, so the crosshair answers to the keyboard as well as the mouse. */}
        <div className="absolute inset-0 flex">
          {points.map((point, index) => (
            <button
              key={point.month}
              type="button"
              onMouseEnter={() => setHovered(index)}
              onMouseLeave={() => setHovered(null)}
              onFocus={() => setHovered(index)}
              onBlur={() => setHovered(null)}
              className="h-full flex-1"
              title={`${point.label}: ${fmt(point.net)}`}
            >
              <span className="sr-only">
                {point.label}: {fmt(point.net)}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-baseline justify-between text-[11px] text-muted">
        <span>{points[0]?.label}</span>
        <span>{points.at(-1)?.label}</span>
      </div>
    </div>
  )
}
